/* eslint-disable react-hooks/exhaustive-deps */
import Mapa from '@componentes/Mapa/Mapa';
import { useEffect, useState } from 'react';
import instance from 'api/instance';
import useToggle from '@utils/useToggle';
import { fetchConfig } from 'api/configService';
import { CamaraData, CoordinatesData, Tramo } from '@componentes/Tabla/types';
import { obtenerParkingColor } from '@utils/parkingFunctions';
import { Box } from '@mui/material';
import Temperatura from '@componentes/Temperatura/Temperatura';
import TablaEspecial from '@componentes/TablaEspecial/TablaEspecial';
import EstadoTraficoPrincipal from '@componentes/EstadoTraficoPrincipal/EstadoTraficoPrincipal';
import IncidenciasPrincipal from '@componentes/IncidenciasPrincipal/IncidenciasPrincipal';
import { useWindowSize } from '@utils/resize';
import { ObtenerEstadoPilona } from '@utils/estadoPilona';
import { adjustDuplicateCoordinates } from '@utils/duplicateCoordinates';

export default function Principal() {
    const { width } = useWindowSize();
    const [coordinates, setCoordinates] = useState<CoordinatesData[]>([]);
    const [configLoaded, setConfigLoaded] = useToggle(false);
    const [apiUrlConfig, setApiUrlConfig] = useState('');
    const [infoAparcamiento, setInfoAparcamiento] = useState<{ id: number, descripcion: string, libres: string, nivel: number, forzadura: number }[]>([]);
    const [infoIncidencias, setInfoIncidencias] = useState<{ id: number, description: string, image: string, preaviso: number, fechaInicio: string, fechaFin: string }[]>([]);
    const [info, setInfo] = useState<Tramo[]>([]);
    const currentDate = new Date();

    useEffect(() => {
        fetchConfig(setApiUrlConfig, setConfigLoaded);
    }, []);

    useEffect(() => {
        if (configLoaded) {
            const fetchData = async () => {
                const apiUrlCamaras = `${apiUrlConfig}obtenerDatosCamaras`;
                const apiUrlParking = `${apiUrlConfig}listadoParking`;
                const apiUrlIncidencia = `${apiUrlConfig}obtenerIncidencias`;
                const apiUrlPilonas = `${apiUrlConfig}getPilonas`;
                const apiUrlPaneles = `${apiUrlConfig}getPaneles`;
                const apiUrlSenales = `${apiUrlConfig}getSenales`;
                const apiUrl = `${apiUrlConfig}infoTramos`;
                try {

                    const [responseCamaras, responseParking, responseIncidencia, responseTramos, responsePilonas, responsePaneles, responseSenales] = await Promise.all([
                        instance.get<CamaraData[]>(apiUrlCamaras).catch(() => ({ data: [] })),
                        instance.get<any[]>(apiUrlParking).catch(() => ({ data: [] })),
                        instance.get<any[]>(apiUrlIncidencia).catch(() => ({ data: [] })),
                        instance.get(apiUrl).catch(() => ({ data: [] })),
                        instance.get<any[]>(apiUrlPilonas).catch(() => ({ data: [] })),
                        instance.get<any[]>(apiUrlPaneles).catch(() => ({ data: [] })),
                        instance.get<any[]>(apiUrlSenales).catch(() => ({ data: [] }))
                    ]);
                    const coor_camaras = responseCamaras.data.filter(ele => ele.ESTA_OPERATIVA === 1).map(ele => ({
                        id: ele.CODIGO,
                        lng: ele.LONGITUD,
                        lat: ele.LATITUD,
                        description: ele.DESCRIPCION,
                        image: 'camara'
                    }));

                    const coor_pilonas = responsePilonas.data.map(pilona => ({
                        id: pilona.CODIGO,
                        lng: pilona.LONGITUD,
                        lat: pilona.LATITUD,
                        description: pilona.DESCRI,
                        image: ObtenerEstadoPilona(pilona.ESTADO).imagen,
                        estado: pilona.ESTADO,
                        desc_estado: pilona.DESC_ESTADO,
                    }));

                    const coor_parking = responseParking.data.map(parking => ({
                        id: parking.CODIGO,
                        lng: parking.LONGITUD,
                        lat: parking.LATITUD,
                        description: parking.DESCRIPCION,
                        image: parking.LIBRES === null ? 'privado' : obtenerParkingColor(parking.NIVEL),
                        direccion: parking.DIRECCION,
                        plazasLibres: parking.LIBRES,
                        plazasTotales: parking.TOTALES,
                        forzadura: parking.FORZADURA,
                        nivel: parking.NIVEL
                    }));
                    setInfoAparcamiento(responseParking.data.map(ele => ({ id: ele.CODIGO, descripcion: ele.DESCRIPCION, libres: ele.LIBRES, nivel: ele.NIVEL, forzadura: ele.FORZADURA })));

                    const incidencias = responseIncidencia.data.filter(incidencia => {
                        const fechaFin = new Date(incidencia.FECHA_FIN);
                        const fechaInicio = new Date(incidencia.FECHA_INICIO);
                        const fechaInicioPreaviso = new Date(fechaInicio);
                        fechaInicioPreaviso.setDate(fechaInicioPreaviso.getDate() - incidencia.PREAVISO);
                        return fechaFin > currentDate && currentDate > fechaInicioPreaviso;
                    });

                    const coor_incidencia = incidencias.map(incidencia => ({
                        id: incidencia.CODIGO,
                        lng: incidencia.LONGITUD,
                        lat: incidencia.LATITUD,
                        description: incidencia.DESCRIPCION_EVENTO,
                        image: `incidencia${incidencia.TIPO_EVENTO}`,
                        fechaInicio: incidencia.FECHA_INICIO,
                        fechaFin: incidencia.FECHA_FIN
                    }));
                    setInfoIncidencias(responseIncidencia.data.map(incidencia => ({
                        id: incidencia.ID_EVENTO,
                        description: incidencia.DESCRIPCION_EVENTO,
                        image: `incidencia${incidencia.TIPO_EVENTO}.png`,
                        preaviso: incidencia.PREAVISO,
                        fechaInicio: incidencia.FECHA_INICIO,
                        fechaFin: incidencia.FECHA_FIN
                    })));

                    setInfo(responseTramos.data);
                    const coor_paneles = await Promise.all(responsePaneles.data.map(async (ele) => {
                        const apiUrlGif = `${apiUrlConfig}getGifsPaneles/${ele.CODIGO}`;
                        const response = await instance.get(apiUrlGif, { responseType: 'arraybuffer' }).catch(() => ({ data: [] }));

                        if (response.data) {
                            const gifData = new Uint8Array(response.data);
                            const base64Gif = btoa(String.fromCharCode(...Array.from(gifData)));
                            return {
                                id: ele.CODIGO,
                                lng: ele.LONGITUD,
                                lat: ele.LATITUD,
                                isPanel: true,
                                nombre: ele.DESCRIPCION,
                                image: `data:image/gif;base64,${base64Gif}`,
                                estado: ele.ESTADO,
                                forzadura: ele.FORZADURA
                            };
                        }
                    }));
                    const coor_senales = responseSenales.data.map(ele => ({
                        id: ele.CODIGO,
                        lng: ele.LONGITUD,
                        lat: ele.LATITUD,
                        nombre: ele.DESCRIPCION,
                        image: ele.IMAGEN,
                        estado: ele.ESTADO
                    }));

                    const allCoordinates = [...coor_camaras, ...coor_parking, ...coor_incidencia, ...coor_pilonas, ...coor_paneles, ...coor_senales];
                    const uniqueCoordinates = adjustDuplicateCoordinates(allCoordinates);
                    setCoordinates(uniqueCoordinates);
                } catch (error) {
                    console.error("Error al hacer la solicitud:", error);
                }
            };

            fetchData();

        }
    }, [configLoaded, apiUrlConfig]);

    return (
        <>
            {width > 1300 &&
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div style={{ width: '28%' }}>
                            <Temperatura />
                        </div>
                        <div style={{ width: '70%' }}>
                            <IncidenciasPrincipal info={infoIncidencias} />
                        </div>
                    </Box>

                    {
                        coordinates.length > 0 && info.length > 0 && <div className="principal" style={{ marginBottom: '20px', marginTop: '20px', display: 'flex', justifyContent: 'space-between' }}>
                            <div style={{ width: '70%' }}>
                                <Mapa coordinates={coordinates} info={info} isPrincipal />
                            </div>
                            <div style={{ width: '28%' }}>
                                <EstadoTraficoPrincipal info={info} />

                                <div style={{ marginTop: '4%' }}>
                                    < TablaEspecial info={infoAparcamiento} />

                                </div>
                            </div>
                        </div>
                    }
                </Box>
            }
            {width < 1300 &&
                <Box sx={{ maxWidth: '100vw' }}>
                    <Box>
                        <div style={{ width: '100%' }}>
                            <Temperatura />
                        </div>
                        <div style={{ width: '100%', marginTop: '4%' }}>
                            <IncidenciasPrincipal info={infoIncidencias} />
                        </div>
                    </Box>

                    {
                        coordinates.length > 0 && info.length > 0 && <div className="principal" style={{ marginBottom: '20px', marginTop: '20px' }}>
                            <div style={{ width: '100%' }}>
                                <Mapa coordinates={coordinates} info={info} isPrincipal />
                            </div>
                            <div style={{ width: '100%' }}>
                                <div style={{ marginTop: '4%' }}>
                                    <EstadoTraficoPrincipal info={info} />
                                </div>
                                < TablaEspecial info={infoAparcamiento} />
                            </div>
                        </div>
                    }
                </Box>
            }
        </>
    );
}